import { useState } from "react";
import DOMPurify from "dompurify";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

const clean = (h) => DOMPurify.sanitize(h || "", { ADD_ATTR: ["target"] }).replace(/src="(\/api\/[^"]+)"/g, (_, u) => `src="${imgUrl(u)}"`);
const stripP = (h) => (h || "").replace(/^<p>/, "").replace(/<\/p>$/, "").replace(/<\/p><p>/g, "<br>");

export const Html = ({ html, inline, className = "", as: T = "div", ...rest }) => {
  if (!html) return null;
  return <T className={inline ? className : `rt ${className}`} dangerouslySetInnerHTML={{ __html: clean(inline ? stripP(html) : html) }} {...rest} />;
};

export const SmartLink = ({ to, children, ...p }) => (/^(https?:|mailto:|tel:)/.test(to || "")
  ? <a href={to} target="_blank" rel="noreferrer" {...p}>{children}</a> : <Link to={to || "/"} {...p}>{children}</Link>);

export const Btn = ({ text, to, light, outline, gold, testid }) => (text ? (
  <SmartLink to={to} data-testid={testid}
    className={outline ? `btn-line ${light ? "border-seta/50 text-seta hover:!border-oro hover:!bg-seta hover:!text-ink" : ""}`
      : gold || light ? "btn-gold" : "btn-ink"}>
    {text} {!outline && <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />}
  </SmartLink>) : null);

export const TextLink = ({ text, to, testid, className = "" }) => (text ? (
  <SmartLink to={to} className={`group inline-flex items-center gap-2.5 text-[12.5px] uppercase tracking-[0.14em] text-ink link-u ${className}`} data-testid={testid}>{text} <ArrowRight size={14} strokeWidth={1.4} className="text-oro transition-transform duration-500 group-hover:translate-x-1" /></SmartLink>) : null);

export const Head = ({ d }) => (d.eyebrow || d.title ? (
  <Reveal className="flex flex-wrap items-end justify-between gap-8 mb-16 sm:mb-20 border-b border-line pb-8">
    <div className="max-w-3xl">{d.eyebrow && <p className="eyebrow mb-7 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
      <Html inline as="h2" html={d.title} className="font-serif font-light text-4xl sm:text-[54px] leading-[1.06] tracking-[-0.025em] [&_em]:italic [&_em]:text-oliva" />
      {d.intro && <p className="mt-6 text-[15.5px] font-light text-stoneish leading-[1.85] max-w-xl">{d.intro}</p>}</div>
    <TextLink text={d.link_text} to={d.link} className="hidden sm:inline-flex shrink-0" />
  </Reveal>) : null);

/** Immagine con caricamento differito e sfumatura dal placeholder sfocato. */
export const Img = ({ src, alt, className = "", eager, ...rest }) => {
  const [loaded, setLoaded] = useState(false);
  if (!src) return null;
  return (
    <img src={imgUrl(src)} alt={alt || ""} loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : undefined} decoding="async"
      ref={(el) => { if (el?.complete) setLoaded(true); }} onLoad={() => setLoaded(true)} onError={() => setLoaded(true)}
      className={`img-fade ${loaded ? "is-loaded" : ""} ${className}`} {...rest} />
  );
};

export const bg = (d) => (d.background === "ink" ? "sec-dark" : d.background === "cream" ? "bg-sabbia" : "");
